import classNames from "classnames";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import {
  useExpanded,
  useFilters,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from "react-table";
import { useLocalStorage } from "react-use";

import Spinner from "components/Spinner";
import ApplicationState from "store/ApplicationState";

import SlidesColumns from "./SlidesColumns";
import SlidesFilters from "./SlidesFilters";
import { SlidesTableProps } from "./SlidesTable";
import SlidesTableView from "./SlidesTableView";
import AdminPagination from "../casesTable/Pagination";
import EditSlidePanel from "../editSlidePanel/EditSlidePanel";
import { AdminPageSizeStorage, Filter } from "../types/adminTypes";

const FilterableSlidesTable = ({ columns }: SlidesTableProps) => {
  const location = useLocation();

  const slides = useSelector(({ admin }: ApplicationState) => admin.slides);
  const slide_id = parseInt(useParams<{ slide_id: string }>().slide_id || "0");

  const usp = new URLSearchParams(location.search);
  const initialFilters = useMemo(() => {
    return [...usp.entries()].reduce(
      (
        acc: {
          filters: Filter;
          pageIndex?: number;
          sortBy?: { id: string; desc: boolean }[];
        },
        [id, value]
      ) => {
        if (id === "sortBy") {
          acc.sortBy = value
            ? value[0] === "-"
              ? [{ id: value.slice(1), desc: true }]
              : [{ id: value, desc: false }]
            : undefined;
        } else if (id === "page") {
          acc.pageIndex = parseInt(value) - 1;
        } else {
          acc.filters[id] = value;
        }
        return acc;
      },
      { filters: {} }
    );
  }, [location]);

  const [storagePageSize, setStoragePageSize] =
    useLocalStorage<AdminPageSizeStorage>("AdminPageSize");

  const setPageSizeToStorage = (value: string) => {
    setStoragePageSize({
      ...storagePageSize,
      slides: parseInt(value),
    });
  };

  const {
    headerGroups,
    rows,
    prepareRow,
    toggleAllRowsExpanded,

    // Pagination
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,

    // Selection
    state: { sortBy, pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: slides.results,
      defaultColumn: {
        sortDescFirst: true,
      },
      manualSortBy: true,
      manualFilters: true,
      autoResetFilters: false,
      autoResetExpanded: false,
      manualPagination: true,
      pageCount: -1,

      initialState: {
        pageSize: storagePageSize?.slides || 25,
        sortBy: initialFilters.sortBy || [{ id: "created_at", desc: false }],
        pageIndex: initialFilters.pageIndex || 0,
      },
    },

    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );

  const pageCount = slides?.count ? Math.ceil(slides.count / pageSize) : -1;

  return (
    <div className="AdminCaselist">
      {slide_id ? (
        <EditSlidePanel
          slideId={slide_id}
          returnUrl={`/admin/slides${location.search}`}
        />
      ) : undefined}
      <SlidesFilters
        sortBy={sortBy}
        pageSize={pageSize}
        pageIndex={pageIndex}
        toggleAllRowsExpanded={toggleAllRowsExpanded}
        setPage={gotoPage}
        initialFilters={initialFilters.filters}
      />
      <div className={classNames({ "AdminCaselist--loading": slides.loading })}>
        {slides.loading && (
          <div className="AdminCaselist__spinner">
            <Spinner />
          </div>
        )}
        <SlidesTableView
          headerGroups={headerGroups}
          rows={rows}
          prepareRow={prepareRow}
          lastViewed={slides.lastViewed}
          asyncSlideDetail
        />
        <AdminPagination
          {...{
            nextPage,
            previousPage,
            gotoPage,
            canPreviousPage,
            canNextPage: canNextPage && !!slides.next,
            totalCount: slides.count,

            pageIndex,
            pageCount,
            pageSize,
            setPageSize,

            toggleAllRowsExpanded,

            label: "slides",
            setToLocalStorage: setPageSizeToStorage,
          }}
        />
      </div>
    </div>
  );
};

export default SlidesColumns(FilterableSlidesTable, {
  displayCaseInfo: true,
  hideSlideRetention: true,
  setLastViewed: true,
});
